// portal.repo — query untuk PORTAL SUPPLIER (read-only fase 1). RLS (0046) otomatis batasi ke supplier login.
import { supabase, pat } from '@core/supabase';
import { fetchAllPaged } from '@core/paging';
import { serahTotal, paidTotal, type Serah, type ReturEvent, type ReqPayment } from '@domain/sparepart';
import type { ImportRow } from '@domain/sparepartImport';

export interface PortalReq {
  id: number; ticket_no: string; outlet: string | null; supplier_id: number | null; items: string[];
  serah: Serah | null; batal: boolean; created_at: string; by_user: string | null;
  payments: ReqPayment[]; ticketUnit: string; ticketTeknisi: string | null;
  /** Pesanan indent (barang belum ada) + jawaban supplier. */
  indent: boolean; indent_eta: string | null; indent_note: string | null;
  /** Foto part dari teknisi (bucket 'sparepart') — supaya supplier tak salah kirim. */
  foto: string[];
  /** Titipan dari supplier lain sesama cabang (0124). */
  parent_req_id: number | null; titip_ket: string | null; tolak_ket: string | null; supplier_nama: string | null;
  /** Konfirmasi penerimaan teknisi (0128). null = sudah diserahkan tapi belum diterima. */
  diterima_at: string | null; diterima_by: string | null; ingat_at: string | null;
}
export interface PortalPart { id: number; barcode: string | null; nama: string; kategori: string | null; stok: number; harga: number }

// TIDAK embed tabel tickets (RLS row-level akan expose kolom sensitif spt sandi/PIN). Unit/teknisi via RPC aman.
const REQ_SEL = 'id,ticket_no,outlet,supplier_id,supplier_nama,items,serah,batal,indent,indent_eta,indent_note,foto,parent_req_id,titip_ket,tolak_ket,diterima_at,diterima_by,ingat_at,created_at,by_user,req_payments(amount,method,note,by_user,created_at)';

/** Semua request ke supplier ini (RLS → cuma miliknya). Urut terbaru. Unit/teknisi via supplier_ticket_meta RPC (kolom aman). */
export async function myRequests(): Promise<PortalReq[]> {
  const data = await fetchAllPaged<unknown>((a, b) => supabase().from('sparepart_requests')
    .select(REQ_SEL).order('created_at', { ascending: false }).order('id').range(a, b));
  const meta = new Map<string, { unit: string | null; teknisi: string | null }>();
  const { data: md, error: me } = await supabase().rpc('supplier_ticket_meta', { p_ticket_no: null });
  if (me) throw new Error(me.message);
  for (const t of (md ?? []) as { no: string; unit: string | null; teknisi: string | null }[]) meta.set(t.no, { unit: t.unit, teknisi: t.teknisi });
  return data.map((r) => {
    const x = r as { id: number; ticket_no: string; outlet: string | null; supplier_id: number | null; supplier_nama?: string | null; items: unknown; serah: unknown; batal: boolean | null; indent?: boolean | null; indent_eta?: string | null; indent_note?: string | null; foto?: unknown; parent_req_id?: number | null; titip_ket?: string | null; tolak_ket?: string | null; diterima_at?: string | null; diterima_by?: string | null; ingat_at?: string | null; created_at: string; by_user: string | null; req_payments?: { amount: number; method: string | null; note: string | null; by_user: string | null; created_at: string }[] };
    const tk = meta.get(x.ticket_no);
    return {
      id: x.id, ticket_no: x.ticket_no, outlet: x.outlet, supplier_id: x.supplier_id ?? null, items: Array.isArray(x.items) ? (x.items as string[]) : [],
      serah: (x.serah ?? null) as Serah | null, batal: x.batal ?? false, created_at: x.created_at, by_user: x.by_user,
      indent: x.indent ?? false, indent_eta: x.indent_eta ?? null, indent_note: x.indent_note ?? null,
      parent_req_id: x.parent_req_id ?? null, titip_ket: x.titip_ket ?? null, tolak_ket: x.tolak_ket ?? null,
      supplier_nama: x.supplier_nama ?? null,
      diterima_at: x.diterima_at ?? null, diterima_by: x.diterima_by ?? null, ingat_at: x.ingat_at ?? null,
      foto: Array.isArray(x.foto) ? (x.foto as string[]) : [],
      payments: (x.req_payments ?? []).map((p) => ({ amount: p.amount, method: p.method ?? undefined, note: p.note ?? undefined, by: p.by_user ?? undefined, created_at: p.created_at })),
      ticketUnit: tk?.unit ?? '', ticketTeknisi: tk?.teknisi ?? null,
    };
  });
}

/** Katalog sparepart supplier ini (RLS → miliknya), halaman pertama saja.
 *  PostgREST memotong di 1.000 baris tanpa memberi tanda; katalog impor bisa 10.000 SKU →
 *  batasnya dibuat tegas dan jumlah sebenarnya diambil terpisah agar bisa diberitahukan. */
export const LIST_MAX = 1000;   // batas aman PostgREST; sisanya lewat pencarian (katalog bisa 10.000+ SKU)
export async function mySpareparts(limit = LIST_MAX): Promise<PortalPart[]> {
  const { data, error } = await supabase().from('spareparts').select('id,barcode,nama,kategori,stok,harga').order('nama').limit(limit);
  if (error) throw new Error(error.message);
  return (data ?? []) as PortalPart[];
}

/** Jumlah sebenarnya di database (bukan yang tampil). `term` diisi → jumlah HASIL PENCARIAN. */
export async function countMySpareparts(term = ''): Promise<number> {
  const t = term.trim();
  let q = supabase().from('spareparts').select('id', { count: 'exact', head: true });
  if (t) q = q.or(`nama.ilike.${pat(t)},barcode.ilike.${pat(t)}`);
  const { count, error } = await q;
  if (error) throw new Error(error.message);
  return count ?? 0;
}

/**
 * Hapus SELURUH hasil (bukan hanya yang tampil di layar). Filternya PERSIS sama dengan
 * `searchMySpareparts`, jadi yang terhapus benar-benar yang dicari — termasuk baris ke-1001 dst
 * yang tak muat di layar (Apo 29 Jul: "search batterai og → select semua → yang terhapus semua
 * baterai og"). `term` kosong = seluruh katalog. RLS tetap mengurung ke milik supplier ini.
 */
export async function deleteAllMine(term = ''): Promise<number> {
  const t = term.trim();
  let q = supabase().from('spareparts').delete({ count: 'exact' });
  q = t ? q.or(`nama.ilike.${pat(t)},barcode.ilike.${pat(t)}`) : q.gte('id', 0);  // tanpa filter PostgREST menolak DELETE
  const { count, error } = await q;
  if (error) throw new Error(error.message);
  return count ?? 0;
}

/** Cari di SELURUH katalog sendiri (bukan cuma yang tampil) — jalan menuju SKU ke-1001 dst. */
export async function searchMySpareparts(term: string): Promise<PortalPart[]> {
  const t = term.trim();
  if (!t) return mySpareparts();
  const { data, error } = await supabase().from('spareparts').select('id,barcode,nama,kategori,stok,harga')
    .or(`nama.ilike.${pat(t)},barcode.ilike.${pat(t)}`).order('nama').limit(LIST_MAX);
  if (error) throw new Error(error.message);
  return (data ?? []) as PortalPart[];
}

/** Input manual 1 sparepart oleh supplier. supplier_id dipaksa trigger 0047. */
export async function addPart(d: { barcode: string; nama: string; kategori: string; stok: number; harga: number }): Promise<void> {
  const { error } = await supabase().from('spareparts').insert({ barcode: d.barcode || null, nama: d.nama, kategori: d.kategori || null, stok: d.stok, harga: d.harga });
  if (error) throw new Error(error.message);
}

/** Edit 1 sparepart milik supplier (RLS write_sp + trigger paksa supplier_id). */
export async function updatePart(id: number, d: { barcode: string; nama: string; kategori: string; stok: number; harga: number }): Promise<void> {
  const { error } = await supabase().from('spareparts').update({ barcode: d.barcode || null, nama: d.nama, kategori: d.kategori || null, stok: d.stok, harga: d.harga }).eq('id', id);
  if (error) throw new Error(error.message);
}

/** Hapus 1 sparepart milik supplier (RLS write_sp scope supplier sendiri). */
/** Hapus banyak sparepart sekaligus — dipotong agar URL filter `in()` tak kepanjangan. */
export async function deleteParts(ids: number[]): Promise<number> {
  let hapus = 0;
  for (let i = 0; i < ids.length; i += 200) {
    const bagian = ids.slice(i, i + 200);
    const { error } = await supabase().from('spareparts').delete().in('id', bagian);
    if (error) throw new Error(`${error.message} (setelah ${hapus} baris terhapus)`);
    hapus += bagian.length;
  }
  return hapus;
}

export async function deletePart(id: number): Promise<void> {
  const { error } = await supabase().from('spareparts').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

export interface ImportResult { inserted: number; updated: number; unchanged: number; skipped: number; duplicates: number; total: number }

const CHUNK = 2000; // baris per kiriman — tahan 10.000+ SKU tanpa payload raksasa

/** Impor katalog lewat RPC import_spareparts (0086): pencocokan barcode & tulis dilakukan DB
 *  set-based, jadi 10.000 baris = beberapa kiriman, bukan 10.000 permintaan.
 *  `dryRun` → hitung saja, tak menulis apa pun (dipakai tombol Pratinjau).
 *  Baris HARUS sudah didedup (dedupByBarcode) agar barcode sama tak terbelah antar-kiriman. */
export async function importSpareparts(rows: ImportRow[], dryRun: boolean, onProgress?: (done: number, total: number) => void): Promise<ImportResult> {
  const sb = supabase();
  const acc: ImportResult = { inserted: 0, updated: 0, unchanged: 0, skipped: 0, duplicates: 0, total: 0 };
  for (let i = 0; i < rows.length; i += CHUNK) {
    const part = rows.slice(i, i + CHUNK);
    const { data, error } = await sb.rpc('import_spareparts', { p_rows: part, p_supplier_id: null, p_dry_run: dryRun });
    if (error) {
      // Tiap potongan = transaksi sendiri, jadi potongan sebelumnya SUDAH masuk. Impor ini aman
      // diulang (cocok by barcode, tak menumpuk), jadi arahkan ke sana, bukan bikin panik.
      const sudah = dryRun ? '' : ` ${i} baris pertama sudah masuk — ulangi impor file yang SAMA (aman, tidak dobel).`;
      throw new Error(`Gagal di baris ${i + 1}–${Math.min(i + CHUNK, rows.length)} dari ${rows.length}: ${error.message}.${sudah}`);
    }
    const r = ((data ?? []) as ImportResult[])[0];
    if (r) for (const k of Object.keys(acc) as (keyof ImportResult)[]) acc[k] += r[k] ?? 0;
    onProgress?.(Math.min(i + CHUNK, rows.length), rows.length);
  }
  return acc;
}

export interface ImportStamp { at: string; by: string; info: string }

/** Catatan import terakhir supplier ini (null bila belum pernah). RPC 0085 — read_sup buka semua
 *  baris supplier, jadi klien tak bisa menyaring "baris saya" sendiri. */
export async function myImportStamp(): Promise<ImportStamp | null> {
  const { data, error } = await supabase().rpc('my_import_stamp');
  if (error) throw new Error(error.message);
  const r = ((data ?? []) as { at: string | null; by_user: string | null; info: string | null }[])[0];
  return r?.at ? { at: r.at, by: r.by_user ?? '?', info: r.info ?? '' } : null;
}

/** Stempel waktu+pelaku import (RPC 0085; kolom lain tabel suppliers tetap tak bisa ditulis supplier). */
export async function markImport(info: string): Promise<void> {
  const { error } = await supabase().rpc('mark_sparepart_import', { p_info: info });
  if (error) throw new Error(error.message);
}

export { serahTotal, paidTotal };
export type { ReturEvent };

// ---------- Riwayat impor & pembatalan (migrasi 0113/0114) ----------
// ---------- Menagih iFiCs (piutang supplier) ----------
export interface TagihInfo { id: number; nama: string; outlet: string | null; tagih_at: string | null; tagih_amount: number | null }
/** Data supplier yang login (RLS 0095 → hanya barisnya sendiri). null bila akun belum tertaut. */
export async function myTagihInfo(): Promise<TagihInfo | null> {
  const { data, error } = await supabase().from('suppliers').select('id,nama,outlet,tagih_at,tagih_amount').limit(1).maybeSingle();
  if (error) throw new Error(error.message);
  return (data as TagihInfo | null) ?? null;
}
export interface TagihHasil { sisa: number; nota: number; tagih_at: string }
/** Kirim tagihan gabungan ke Finance. Nominal & jeda ditegakkan RPC (0118), bukan klien. */
export async function tagihHutang(): Promise<TagihHasil> {
  const { data, error } = await supabase().rpc('tagih_hutang_supplier');
  if (error) throw new Error(error.message);
  const r = (Array.isArray(data) ? data[0] : data) as TagihHasil | undefined;
  if (!r) throw new Error('Tagihan tak terkirim — coba lagi.');
  return { sisa: Number(r.sisa), nota: Number(r.nota), tagih_at: r.tagih_at };
}

export interface ImportBatch { id: number; at: string; by_user: string | null; jumlah: number; dibatalkan: boolean; dibatalkan_at: string | null }

/** 10 batch impor terakhir (retensi diurus di DB saat impor). RLS: katalog sendiri. */
export async function listImportBatches(): Promise<ImportBatch[]> {
  const { data, error } = await supabase().from('sparepart_import_batches')
    .select('id,at,by_user,jumlah,dibatalkan,dibatalkan_at').order('at', { ascending: false }).limit(10);
  if (error) throw new Error(error.message);
  return (data ?? []) as ImportBatch[];
}

export interface RollbackHasil { dikembalikan: number; dihapus: number; dilewati: number }
/** Batalkan satu impor. Baris yang sudah berubah lagi setelah impor DILEWATI (aturan Apo). */
export async function rollbackImport(batchId: number): Promise<RollbackHasil> {
  const { data, error } = await supabase().rpc('rollback_import_batch', { p_batch: batchId });
  if (error) throw new Error(error.message);
  const r = ((data ?? []) as RollbackHasil[])[0];
  return r ?? { dikembalikan: 0, dihapus: 0, dilewati: 0 };
}

/** Nomor WA Finance satu cabang (RPC 0120) — peran Supplier tak boleh membaca tabel settings. */
export async function waFinance(outlet: string | null): Promise<{ label: string; wa: string }[]> {
  if (!outlet) return [];
  const { data, error } = await supabase().rpc('wa_kantor_finance', { p_outlet: outlet });
  if (error) throw new Error(error.message);
  return ((data ?? []) as { label: string; wa: string }[]).filter((n) => (n.wa ?? '').replace(/\D/g, '').length >= 8);
}

/**
 * Jumlah request yang menunggu ditindak supplier ini → badge angka di menu 📥 Request Masuk.
 * Tanpa badge, satu-satunya cara tahu ada pekerjaan baru adalah membuka menunya (keluhan Apo).
 * Indent yang sudah dijawab TIDAK dihitung — ia menunggu barang, bukan menunggu supplier.
 */
export async function reqMasukCount(): Promise<number> {
  const { count, error } = await supabase().from('sparepart_requests')
    .select('id', { count: 'exact', head: true })
    // `.eq('batal', false)` akan MEMBUANG baris yang batal-nya NULL (kolomnya nullable), padahal
    // layar menganggap null = belum batal — badge lalu lebih kecil daripada isi layar.
    .is('serah', null).not('batal', 'is', true).is('indent_eta', null);
  if (error) return 0;
  return count ?? 0;
}

// ---------- Titipan sesama cabang (0124) ----------
/** Supplier lain di cabang yang SAMA (RPC — akun supplier tak boleh membaca daftar suppliers). */
export async function supplierSeCabang(): Promise<{ id: number; nama: string }[]> {
  const { data, error } = await supabase().rpc('supplier_se_cabang');
  if (error) throw new Error(error.message);
  return (data ?? []) as { id: number; nama: string }[];
}

/** Titipkan sebagian barang ke supplier se-cabang → request turunan baru. */
export async function titipSparepart(reqId: number, supplierTujuan: number, items: string[], catatan: string): Promise<number> {
  const { data, error } = await supabase().rpc('titip_sparepart', {
    p_req_id: reqId, p_supplier_tujuan: supplierTujuan, p_items: items, p_catatan: catatan || null,
  });
  if (error) throw new Error(error.message);
  return Number(data);
}

/** Tolak titipan (penerima juga kosong) — pengirim diberi tahu oleh RPC. */
export async function tolakTitipan(reqId: number, ket: string): Promise<void> {
  const { error } = await supabase().rpc('tolak_titipan', { p_req_id: reqId, p_ket: ket });
  if (error) throw new Error(error.message);
}

/** Ingatkan teknisi supaya mengonfirmasi penerimaan (RPC 0128, berjeda 6 jam). */
export async function ingatkanTerima(reqId: number): Promise<void> {
  const { error } = await supabase().rpc('ingatkan_terima', { p_req_id: reqId });
  if (error) throw new Error(error.message);
}
